import { AvailableIcon } from '../../../../../components/atoms/Icon';

export type PlanDetailsType = {
  designedFor: string;
  bestSuitedFor: string;
  mediaScope: string;
  modelRefresh: string;
  coreInclusions: Record<string, boolean>;
  fullFeatures: Record<string, boolean>;
  addOns: string[];
};

export type PricingPlansType = {
  'MMM Starter': PlanDetailsType;
  'DIGITAL GROWTH': PlanDetailsType;
  'OMNICHANNEL GROWTH': PlanDetailsType;
  'ENTERPRISE SCALE': PlanDetailsType;
};

export const pricingPlans: PricingPlansType = {
  'MMM Starter': {
    designedFor:
      'For marketers who want to understand what really drives performance before scaling decisions',
    bestSuitedFor: 'Digital only advertisers, SMBs',
    mediaScope: 'Digital only',
    modelRefresh: 'Monthly',
    coreInclusions: {
      'AI-powered dashboard for digital ROI': true,
      'Modeling based on your own data': true,
      'Auto-generated insights & PDF reports': false,
      'Scenario planning & budget optimization tools': false,
      'Built for the Global digital media environment': true,
    },
    fullFeatures: {
      'Cross-platform digital attribution (Google, Meta, etc.)': true,
      'Daily or weekly data updates': true,
      'Real-time AI analysis using Bayesian models': true,
      'Forecasting & scenario simulation tools': false,
      'Automatic stakeholder-ready PDF reports': false,
      'KPI- and funnel-specific breakdowns': true,
      'Access up to 5 users included': false,
      'Email support': true,
      'Onboarding session': false,
      'Offline media modeling (TV, print, radio, OOH)': false,
      'Seasonal campaign benchmarking': false,
      'Combined flighting & pacing dashboard': false,
      'Funnel-level ROI by media type': false,
      'Internal campaign performance review templates': false,
      'Quarterly strategic alignment call': false,
      'Chat + email support': false,
      '1 brand/business unit included': true,
      'Monthly or quarterly model cycles': true,
      'Multi-region and multi-brand support': false,
      'Advanced pacing dashboard (burst vs. continuous)': false,
      'Long-term brand uplift measurement': false,
      'Strategic insight dashboard (sales trends, ROI shifts)': false,
      'Internal benchmarking against historic campaigns': false,
      'Permission & user role management': true,
      'Onboarding + quarterly strategy review': false,
      'Dedicated success manager': false,
    },
    addOns: [
      'Offline media integration: add other packages',
      'Custom KPI dashboards: from €150/mo',
      'Extra business units or brands: from €400/mo',
      'Forecasting & scenario simulation tools: from €200/mo',
      'Daily or weekly model updates: from €300/mo',
    ],
  },
  'DIGITAL GROWTH': {
    designedFor: 'Digital-first advertisers with clear performance goals',
    bestSuitedFor: 'Ecommerce, mobile apps, digital subscription services',
    mediaScope: 'Digital only',
    modelRefresh: 'Daily or weekly',
    coreInclusions: {
      'AI-powered dashboard for digital ROI': true,
      'Modeling based on your own data': true,
      'Auto-generated insights & PDF reports': true,
      'Scenario planning & budget optimization tools': true,
      'Built for the Global digital media environment': true,
    },
    fullFeatures: {
      'Cross-platform digital attribution (Google, Meta, etc.)': true,
      'Daily or weekly data updates': true,
      'Real-time AI analysis using Bayesian models': true,
      'Forecasting & scenario simulation tools': true,
      'Automatic stakeholder-ready PDF reports': true,
      'KPI- and funnel-specific breakdowns': true,
      'Access up to 5 users included': true,
      'Email support': true,
      'Onboarding session': true,
      'Offline media modeling (TV, print, radio, OOH)': false,
      'Seasonal campaign benchmarking': false,
      'Combined flighting & pacing dashboard': false,
      'Funnel-level ROI by media type': false,
      'Internal campaign performance review templates': false,
      'Quarterly strategic alignment call': false,
      'Chat + email support': false,
      '1 brand/business unit included': true,
      'Monthly or quarterly model cycles': false,
      'Multi-region and multi-brand support': false,
      'Advanced pacing dashboard (burst vs. continuous)': false,
      'Long-term brand uplift measurement': false,
      'Strategic insight dashboard (sales trends, ROI shifts)': false,
      'Internal benchmarking against historic campaigns': false,
      'Permission & user role management': true,
      'Onboarding + quarterly strategy review': false,
      'Dedicated success manager': false,
    },
    addOns: [
      'Offline media integration: add other packages',
      'Custom KPI dashboards: from €150/mo',
      'Extra business units or brands: from €400/mo',
      'Dedicated analyst support: on request',
      '',
    ],
  },
  'OMNICHANNEL GROWTH': {
    designedFor: 'Hybrid marketers working across media',
    bestSuitedFor: 'Retail chains, auto dealers, local/national brands',
    mediaScope: 'Digital & Offline',
    modelRefresh: 'Twice/year (seasonal updates)',
    coreInclusions: {
      'AI-powered dashboard for digital ROI': true,
      'Modeling based on your own data': true,
      'Auto-generated insights & PDF reports': true,
      'Scenario planning & budget optimization tools': true,
      'Built for the Global digital media environment': true,
    },
    fullFeatures: {
      'Cross-platform digital attribution (Google, Meta, etc.)': true,
      'Daily or weekly data updates': true,
      'Real-time AI analysis using Bayesian models': true,
      'Forecasting & scenario simulation tools': true,
      'Automatic stakeholder-ready PDF reports': true,
      'KPI- and funnel-specific breakdowns': true,
      'Access up to 5 users included': true,
      'Email support': true,
      'Onboarding session': true,
      'Offline media modeling (TV, print, radio, OOH)': true,
      'Seasonal campaign benchmarking': true,
      'Combined flighting & pacing dashboard': true,
      'Funnel-level ROI by media type': true,
      'Internal campaign performance review templates': true,
      'Quarterly strategic alignment call': true,
      'Chat + email support': false,
      '1 brand/business unit included': true,
      'Monthly or quarterly model cycles': false,
      'Multi-region and multi-brand support': false,
      'Advanced pacing dashboard (burst vs. continuous)': false,
      'Long-term brand uplift measurement': false,
      'Strategic insight dashboard (sales trends, ROI shifts)': false,
      'Internal benchmarking against historic campaigns': false,
      'Permission & user role management': true,
      'Onboarding + quarterly strategy review': false,
      'Dedicated success manager': false,
    },
    addOns: [
      'Monthly model refresh: €300/mo',
      'Additional business units: €400/mo each',
      'Data warehousing / integrations: from €250/mo',
      'Agency/partner access: free for up to 2 accounts',
      'Premium support SLA: €150/mo',
    ],
  },
  'ENTERPRISE SCALE': {
    designedFor: 'Complex media environments and large teams',
    bestSuitedFor: 'National brands, agencies, multi-market advertisers',
    mediaScope: 'Digital & Offline',
    modelRefresh: 'Monthly or Quarterly',
    coreInclusions: {
      'AI-powered dashboard for digital ROI': true,
      'Modeling based on your own data': true,
      'Auto-generated insights & PDF reports': true,
      'Scenario planning & budget optimization tools': true,
      'Built for the Global digital media environment': true,
    },
    fullFeatures: {
      'Cross-platform digital attribution (Google, Meta, etc.)': true,
      'Daily or weekly data updates': true,
      'Real-time AI analysis using Bayesian models': true,
      'Forecasting & scenario simulation tools': true,
      'Automatic stakeholder-ready PDF reports': true,
      'KPI- and funnel-specific breakdowns': true,
      'Access up to 5 users included': true,
      'Email support': true,
      'Onboarding session': true,
      'Offline media modeling (TV, print, radio, OOH)': true,
      'Seasonal campaign benchmarking': true,
      'Combined flighting & pacing dashboard': true,
      'Funnel-level ROI by media type': true,
      'Internal campaign performance review templates': true,
      'Quarterly strategic alignment call': true,
      'Chat + email support': true,
      '1 brand/business unit included': true,
      'Monthly or quarterly model cycles': true,
      'Multi-region and multi-brand support': true,
      'Advanced pacing dashboard (burst vs. continuous)': true,
      'Long-term brand uplift measurement': true,
      'Strategic insight dashboard (sales trends, ROI shifts)': true,
      'Internal benchmarking against historic campaigns': true,
      'Permission & user role management': true,
      'Onboarding + quarterly strategy review': true,
      'Dedicated success manager': true,
    },
    addOns: [
      'Custom attribution modeling: from €500/mo',
      'BI tool integrations (e.g. Looker, Power BI): from €250/mo',
      'API access for reporting tools: from €300/mo',
      'Training workshops for teams: €950/session',
      'Extended historical data model migration: one-time quote',
    ],
  },
};

export const planDetails: Array<{
  label: string;
  icon: AvailableIcon;
  key: keyof Omit<PlanDetailsType, 'coreInclusions' | 'fullFeatures'>;
}> = [
  { label: 'Designed for:', icon: 'circle-checkbox', key: 'designedFor' },
  { label: 'Best suited for:', icon: 'graph', key: 'bestSuitedFor' },
  { label: 'Media scope:', icon: 'media', key: 'mediaScope' },
  { label: 'Model refresh:', icon: 'refresh', key: 'modelRefresh' },
];
